import { useCallback, useMemo, useRef, useState } from 'react';

import { useToast } from 'hooks/useToaster';
import { getCustomers } from 'lib/api/getCustomers';
import { getLocations } from 'lib/api/getLocations';
import { getRefNumQualifiers } from 'lib/api/getRefNumQualifiers';
import { RefNumQualifier } from 'lib/api/getRefNumQualifiers';
import { getRefNumRequirements } from 'lib/api/getRefNumRequirements';
import { RefNumRequirement } from 'lib/api/getRefNumRequirements';
import { getServiceOptions } from 'lib/api/getServiceOptions';
import { ServiceOption } from 'lib/api/getServiceOptions';
import { getTransportTypes } from 'lib/api/getTransportTypes';
import { TransportType } from 'lib/api/getTransportTypes';
import { TMSCustomer, TMSLocation } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';

export function useThreeGData(tmsID: Undef<number>) {
  const { toast } = useToast();

  // Data States
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [locations, setLocations] = useState<Maybe<TMSLocation[]>>(null);
  const [transportTypes, setTransportTypes] =
    useState<Maybe<TransportType[]>>(null);
  const [serviceOptions, setServiceOptions] =
    useState<Maybe<ServiceOption[]>>(null);
  const [qualifiers, setQualifiers] = useState<Maybe<RefNumQualifier[]>>(null);
  const [requirements, setRequirements] =
    useState<Maybe<RefNumRequirement[]>>(null);

  // Loading States
  const [loading, setLoading] = useState({
    customers: false,
    locations: false,
    transportTypes: false,
    serviceOptions: false,
    qualifiers: false,
    requirements: false,
  });

  // Race condition protection
  const abortControllers = useRef<Record<string, AbortController>>({});

  const fetchData = useCallback(
    async (
      key: keyof typeof loading,
      apiCall: () => Promise<any>,
      setter: (data: any) => void,
      errorMsg: string
    ) => {
      if (!tmsID) return;

      // Abort previous in-flight request for this specific key
      if (abortControllers.current[key]) {
        abortControllers.current[key].abort();
      }
      abortControllers.current[key] = new AbortController();

      setLoading((prev) => ({ ...prev, [key]: true }));

      try {
        const res = await apiCall();
        if (res.isOk()) {
          setter(res.value);
        } else {
          toast({
            description: res.error.message || errorMsg,
            variant: 'destructive',
          });
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          toast({ description: errorMsg, variant: 'destructive' });
        }
      } finally {
        setLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [tmsID, toast]
  );

  // Specific Fetchers - wrapped in useCallback to ensure stable references
  const fetchCustomers = useCallback(
    () =>
      fetchData(
        'customers',
        () => getCustomers(tmsID!),
        (val) => setCustomers(val.customerList),
        'Error fetching customers.'
      ),
    [fetchData, tmsID, setCustomers]
  );

  /** 3G form passes only customerExternalTmsId when filtering */
  const fetchLocations = useCallback(
    (force = false, customerExternalTmsId?: string) =>
      fetchData(
        'locations',
        () =>
          getLocations(tmsID!, force, false, customerExternalTmsId, undefined),
        (val) => setLocations(val.locationList),
        'Error fetching locations.'
      ),
    [fetchData, tmsID, setLocations]
  );

  const fetchTransportTypes = useCallback(
    (force = false) =>
      fetchData(
        'transportTypes',
        () => getTransportTypes(tmsID!, force),
        (val) => setTransportTypes(val.transportTypes),
        'Error fetching equipment types.'
      ),
    [fetchData, tmsID, setTransportTypes]
  );

  const fetchServiceOptions = useCallback(
    (force = false) =>
      fetchData(
        'serviceOptions',
        () => getServiceOptions(tmsID!, force),
        (val) => setServiceOptions(val.serviceOptions),
        'Error fetching service options.'
      ),
    [fetchData, tmsID, setServiceOptions]
  );

  const fetchQualifiers = useCallback(
    () =>
      fetchData(
        'qualifiers',
        () => getRefNumQualifiers(tmsID!),
        (val) => setQualifiers(val.qualifiers),
        'Error fetching reference qualifiers.'
      ),
    [fetchData, tmsID, setQualifiers]
  );

  const fetchRequirements = useCallback(
    (customerID: number) =>
      fetchData(
        'requirements',
        () => getRefNumRequirements(tmsID!, undefined, customerID),
        (val) => setRequirements(val.requirements),
        'Error fetching customer requirements.'
      ),
    [fetchData, tmsID, setRequirements]
  );

  // Memoize data object to prevent unnecessary re-renders
  const data = useMemo(
    () => ({
      customers,
      locations,
      transportTypes,
      serviceOptions,
      qualifiers,
      requirements,
    }),
    [
      customers,
      locations,
      transportTypes,
      serviceOptions,
      qualifiers,
      requirements,
    ]
  );

  // Memoize loading object to prevent unnecessary re-renders
  // This ensures the reference only changes when actual loading values change
  const memoizedLoading = useMemo(
    () => ({
      customers: loading.customers,
      locations: loading.locations,
      transportTypes: loading.transportTypes,
      serviceOptions: loading.serviceOptions,
      qualifiers: loading.qualifiers,
      requirements: loading.requirements,
    }),
    [
      loading.customers,
      loading.locations,
      loading.transportTypes,
      loading.serviceOptions,
      loading.qualifiers,
      loading.requirements,
    ]
  );

  // Memoize actions object to prevent unnecessary re-renders
  const actions = useMemo(
    () => ({
      setCustomers,
      fetchCustomers,
      fetchLocations,
      fetchTransportTypes,
      fetchServiceOptions,
      fetchQualifiers,
      fetchRequirements,
      setLocations,
    }),
    [
      setCustomers,
      fetchCustomers,
      fetchLocations,
      fetchTransportTypes,
      fetchServiceOptions,
      fetchQualifiers,
      fetchRequirements,
      setLocations,
    ]
  );

  return {
    data,
    loading: memoizedLoading,
    actions,
  };
}
